import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { recipePath } from "@/lib/slug";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, Image as ImageIcon, UploadSimple, X } from "@phosphor-icons/react";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import PaywallModal from "@/components/PaywallModal";
import SortableList from "@/components/SortableList";

export default function OCRSandbox() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { flags, limits } = useSiteFlags();
  const [scan, setScan] = useState(null);
  const [book, setBook] = useState(null);
  const [cookbooks, setCookbooks] = useState([]);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // See PaywallModal — surfaces a friendly upgrade path on 402 responses.
  const [paywall, setPaywall] = useState({ open: false, message: "" });
  const fileInputRef = useRef(null);
  // Stable row ids for ingredient / instruction lists — required for @dnd-kit
  // to keep the dragged row identifiable across renders. Ids are LOCAL ONLY.
  const ridCounter = useRef(0);
  const [ingIds, setIngIds] = useState([]);
  const [stepIds, setStepIds] = useState([]);
  const newRid = (prefix) => `${prefix}-${ridCounter.current++}`;

  useEffect(() => {
    const raw = sessionStorage.getItem("scan_result");
    if (!raw) { nav("/scan"); return; }
    const p = JSON.parse(raw);
    setScan(p);
    setData({
      title: p.structured.title || "Untitled Recipe",
      creator_name: p.structured.creator_name || (user?.username || user?.name || ""),
      creation_year: p.structured.creation_year || new Date().getFullYear(),
      serves: p.structured.serves || 4,
      // Preserve nulls when the source (scan / URL / paste) didn't extract a
      // time — better to leave blank than to lie with a made-up "15 min prep".
      // The number inputs display "" when the value is null (see the JSX
      // below), so users can type a value themselves if they want to.
      prep_minutes: (p.structured.prep_minutes ?? null),
      cook_minutes: (p.structured.cook_minutes ?? null),
      rest_minutes: (p.structured.rest_minutes ?? null),
      difficulty: p.structured.difficulty || "Medium",
      experiment_level: 50,
      tools: p.structured.tools || [],
      ingredients: (p.structured.ingredients?.length ? p.structured.ingredients : [{ name: "", quantity: 0, unit: "", notes: "" }]),
      instructions: (p.structured.instructions?.length ? p.structured.instructions : [""]),
      // Auto-populate cover image from the source's og:image / twitter:image when
      // importing from a URL — the user gets a finished-looking card immediately.
      images: p.hero_image ? [p.hero_image] : [],
      tags: [],
      is_public: false,
      is_secret: false,
      copyright_certified: false,
      show_nutrition: false,
    });
    // Seed stable row ids alongside the initial ingredients/instructions
    // so drag-reorder + focus retention work from the first render.
    const ingCount = (p.structured.ingredients?.length || 1);
    const stepCount = (p.structured.instructions?.length || 1);
    const startIng = ridCounter.current;
    setIngIds(Array.from({ length: ingCount }, (_, i) => `ing-${startIng + i}`));
    ridCounter.current += ingCount;
    const startStep = ridCounter.current;
    setStepIds(Array.from({ length: stepCount }, (_, i) => `step-${startStep + i}`));
    ridCounter.current += stepCount;

    (async () => {
      const { data: cb } = await api.get("/cookbooks");
      let owned = cb.owned || [];
      const coEdited = cb.co_edited || [];
      // Auto-create "My First Recipe Filer Book" when user has no cookbooks
      if (owned.length === 0 && coEdited.length === 0) {
        try {
          const { data: created } = await api.post("/cookbooks", {
            title: "My First Recipe Filer Book",
            description: "Your starter cookbook — rename me any time.",
            privacy: "public",
          });
          owned = [created];
          toast.success("Created your first cookbook — feel free to rename it.");
        } catch {}
      }
      // Merge owned + co-edited into the destination picker so users can
      // save a scanned recipe straight into a cookbook a friend shared with
      // them (and gave co-editor rights on). View-only invites are excluded.
      const dest = [...owned, ...coEdited];
      setCookbooks(dest);
      // Default destination: the user's Inbox cookbook (so anything imported from outside
      // a chosen cookbook lands in the Inbox first and the user can file it later).
      // If a specific cookbook was preset (e.g. user clicked "Add recipe" from inside a book),
      // honour that. Otherwise prefer the Inbox, then fall back to the first owned cookbook.
      const inbox = owned.find((c) => c.is_system && c.system_kind === "inbox");
      const initial = p.cookbook_id || inbox?.cookbook_id || owned[0]?.cookbook_id || dest[0]?.cookbook_id || null;
      setBook(initial);
    })();
    // NOTE: intentionally do NOT include `user` in the deps. If we did, a
    // post-save `refresh()` (which updates the AuthContext user) would
    // re-run this effect. By then `sessionStorage.scan_result` has been
    // cleared, so the effect would fire `nav("/scan")` on top of our own
    // `nav("/r/…")` — the observed symptom on iOS Safari was landing at
    // /r/… with a blank page. Load-time user data is already captured
    // above via the initial closure.
  }, [nav]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !scan) return <div className="p-10 text-center">Loading…</div>;

  const updateIngredient = (i, key, val) => {
    const arr = [...data.ingredients]; arr[i] = { ...arr[i], [key]: val }; setData({ ...data, ingredients: arr });
  };
  const updateStep = (i, val) => {
    const arr = [...data.instructions]; arr[i] = val; setData({ ...data, instructions: arr });
  };

  const uploadImages = async (files) => {
    if (!files?.length) return;
    if ((data.images?.length || 0) + files.length > 6) {
      toast.error("Up to 6 images per recipe.");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.url);
      }
      setData({ ...data, images: [...(data.images || []), ...uploaded] });
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    setData({ ...data, images: data.images.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    if (!book) { toast.error("Choose a cookbook first"); return; }
    if (data.is_public && !data.copyright_certified) {
      toast.error("Tick the copyright box to publish.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...data,
        cookbook_id: book,
        ingredients: data.ingredients.filter((i) => i.name?.trim()),
        instructions: data.instructions.filter((s) => s.trim()),
        original_scan_url: scan.original_scan_url,
        scan_pages: scan.scan_pages || [],
        parsed_text: scan.raw_text,
        source_type: scan.source_type || null,
        source_url: scan.source_url || null,
        source_name: scan.source_name || null,
        // Non-English scan support — the extractor returns an ISO code on
        // `structured.source_language`. We persist it so the recipe page
        // can offer "Translate to English" when the source isn't English.
        source_language: scan.structured?.source_language || data.source_language || null,
      };
      const { data: recipe } = await api.post("/recipes", payload);
      // Guard: without a recipe_id the SEO slug path becomes "/" and the user
      // lands on Home with no context. Never seen in practice but bail early
      // with a toast rather than silently redirecting away.
      if (!recipe || !recipe.recipe_id) {
        toast.error("Recipe saved, but the server response was malformed. Refresh your Dashboard to find it.");
        setSaving(false);
        return;
      }
      const target = scan.return_to || recipePath(recipe);
      // 1) Navigate FIRST so React unmounts the Sandbox tree before any
      //    downstream side-effects (refresh /auth/me, sessionStorage cleanup)
      //    run. Fixes an iOS Safari race where a post-save re-render of the
      //    Sandbox could fire its "no scan_result → redirect to /scan" branch
      //    after we had already navigated to /r/… — the observed symptom was
      //    the browser landing at /r/… with a blank page.
      sessionStorage.removeItem("scan_result");
      toast.success("Recipe saved!");
      nav(target, { replace: true });
      // 2) Fire-and-forget refresh AFTER nav so the current component is
      //    already being unmounted when /auth/me returns.
      refresh().catch(() => {});
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall({ open: true, message: e?.response?.data?.detail || "" });
      } else {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="grid lg:grid-cols-12 min-h-screen">
        {/* Left: original scan */}
        <div className="lg:col-span-5 bg-rf-ink p-8 lg:p-12 lg:sticky lg:top-0 lg:h-screen overflow-y-auto">
          <p className="overline text-rf-amber mb-3">Original</p>
          <h2 className="font-display text-2xl font-black text-rf-cream mb-6">Compare side-by-side</h2>
          {scan.preview_url ? (
            <img src={scan.preview_url} alt="original" className="w-full rounded-xl mb-6 border-2 border-rf-cream/10" />
          ) : (
            <div className="bg-rf-cream/5 rounded-xl p-12 text-center mb-6">
              <ImageIcon size={48} className="mx-auto text-rf-cream/40 mb-2" />
              <p className="text-rf-cream/60 text-sm">Typed-in recipe — no scan attached.</p>
            </div>
          )}
          {scan.raw_text && (
            <div>
              <p className="overline text-rf-amber mb-2">Raw OCR text</p>
              <pre className="text-sm text-rf-cream/80 whitespace-pre-wrap bg-rf-cream/5 rounded-xl p-4 max-h-80 overflow-y-auto scroll-fade">{scan.raw_text}</pre>
            </div>
          )}
        </div>

        {/* Right: editable form */}
        <div className="lg:col-span-7 p-8 lg:p-12 overflow-y-auto">
          <Link to="/dashboard" className="text-sm font-bold text-rf-orange hover:underline mb-4 inline-block">← Back to dashboard</Link>
          <p className="overline mb-2">Review &amp; confirm</p>
          <h1 className="font-display text-3xl md:text-4xl font-black text-rf-ink mb-6">Polish before saving.</h1>

          <div className="space-y-5">
            <div>
              <label className="overline">Save into cookbook</label>
              <div className="flex gap-2 mt-1">
                <select data-testid="sandbox-cookbook" value={book || ""} onChange={(e) => setBook(e.target.value)} className="input-rf flex-1">
                  {cookbooks.map((c) => <option key={c.cookbook_id} value={c.cookbook_id}>{c.title}{c.is_system && c.system_kind === "inbox" ? " — Inbox (default)" : ""}</option>)}
                </select>
                <button
                  type="button"
                  data-testid="sandbox-new-book"
                  onClick={async () => {
                    const title = window.prompt("Name your new cookbook:", "New Cookbook");
                    if (!title) return;
                    try {
                      const { data: created } = await api.post("/cookbooks", { title, privacy: "public" });
                      setCookbooks([...cookbooks, created]);
                      setBook(created.cookbook_id);
                      toast.success("Cookbook created");
                    } catch { toast.error("Failed to create"); }
                  }}
                  className="btn-outline-ink !py-3 !px-4 !text-sm whitespace-nowrap"
                >+ New book</button>
              </div>
              {(() => {
                const selected = cookbooks.find((c) => c.cookbook_id === book);
                if (selected?.is_system && selected.system_kind === "inbox") {
                  return (
                    <p className="text-xs text-rf-ink-soft mt-1.5 italic" data-testid="sandbox-inbox-hint">
                      Defaults to your Inbox — file into a permanent cookbook later from the recipe&apos;s &quot;Move to →&quot; menu.
                    </p>
                  );
                }
                return null;
              })()}
            </div>

            <div>
              <label className="overline">Title</label>
              <input data-testid="sandbox-title" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} className="input-rf mt-1 !text-2xl !font-display !font-black" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="overline">Serves</label>
                <input data-testid="sandbox-serves" type="number" min="1" value={data.serves} onChange={(e) => setData({ ...data, serves: +e.target.value })} className="input-rf mt-1" />
              </div>
              <div>
                <label className="overline">Prep (min)</label>
                <input
                  data-testid="sandbox-prep"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={data.prep_minutes ?? ""}
                  onChange={(e) => setData({ ...data, prep_minutes: e.target.value === "" ? null : Math.max(0, +e.target.value) })}
                  placeholder="—"
                  className="input-rf mt-1"
                />
              </div>
              <div>
                <label className="overline">Cook (min)</label>
                <input
                  data-testid="sandbox-cook"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={data.cook_minutes ?? ""}
                  onChange={(e) => setData({ ...data, cook_minutes: e.target.value === "" ? null : Math.max(0, +e.target.value) })}
                  placeholder="—"
                  className="input-rf mt-1"
                />
              </div>
              <div>
                <label className="overline">Rest (min)</label>
                <input
                  data-testid="sandbox-rest"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={data.rest_minutes ?? ""}
                  onChange={(e) => setData({ ...data, rest_minutes: e.target.value === "" ? null : Math.max(0, +e.target.value) })}
                  placeholder="—"
                  className="input-rf mt-1"
                  title="Rest / chill / cool / marinate time — auto-detected from the method"
                />
              </div>
              <div>
                <label className="overline">Difficulty</label>
                <select data-testid="sandbox-difficulty" value={data.difficulty} onChange={(e) => setData({ ...data, difficulty: e.target.value })} className="input-rf mt-1">
                  <option>Easy</option><option>Medium</option><option>Hard</option>
                </select>
              </div>
            </div>

            {/* Recompute times — scans the method for phrases like "simmer
                10 min / rest 30 min" and refreshes the three time boxes
                above. Pure regex, no LLM call. Only overwrites empty
                fields — anything the user has typed by hand stays put. */}
            <button
              type="button"
              data-testid="sandbox-recompute-times"
              onClick={async () => {
                try {
                  const { data: t } = await api.post("/recipes/compute-times", { instructions: data.instructions });
                  setData((d) => ({
                    ...d,
                    prep_minutes: d.prep_minutes ?? t.prep_minutes ?? null,
                    cook_minutes: d.cook_minutes ?? t.cook_minutes ?? null,
                    rest_minutes: d.rest_minutes ?? t.rest_minutes ?? null,
                  }));
                  const bits = [];
                  if (t.prep_minutes) bits.push(`prep ${t.prep_minutes}m`);
                  if (t.cook_minutes) bits.push(`cook ${t.cook_minutes}m`);
                  if (t.rest_minutes) bits.push(`rest ${t.rest_minutes}m`);
                  toast.success(bits.length ? `Detected ${bits.join(" · ")}` : "No time phrases found in the method");
                } catch { toast.error("Couldn't recompute times"); }
              }}
              className="text-xs font-bold text-rf-orange hover:underline inline-flex items-center gap-1"
            >
              ↻ Recompute prep / cook / rest from the method
            </button>

            <div>
              <label className="overline">Creator name</label>
              <input data-testid="sandbox-creator" value={data.creator_name} onChange={(e) => setData({ ...data, creator_name: e.target.value })} className="input-rf mt-1" placeholder="Grandma Hilda" />
            </div>

            {/* Tags */}
            {(() => {
              const selectedBook = cookbooks.find((c) => c.cookbook_id === book);
              const cats = selectedBook?.categories || [];
              if (!cats.length) return null;
              return (
                <div>
                  <label className="overline">Tags</label>
                  <p className="text-xs text-rf-ink-soft mt-1 mb-3">Tap to add this recipe to one or more tags from <span className="font-bold">{selectedBook.title}</span>.</p>
                  <div className="flex flex-wrap gap-2">
                    {cats.map((c) => {
                      const on = data.tags.includes(c);
                      return (
                        <button
                          type="button"
                          key={c}
                          data-testid={`sandbox-tag-${c}`}
                          onClick={() => setData({ ...data, tags: on ? data.tags.filter((t) => t !== c) : [...data.tags, c] })}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${on ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
                        >{c}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Image upload */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Recipe photos (up to 6)</label>
                <button
                  type="button"
                  data-testid="sandbox-add-photo"
                  disabled={uploading || (data.images?.length || 0) >= 6}
                  onClick={() => fileInputRef.current?.click()}
                  className="text-rf-orange text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <UploadSimple size={14} /> {uploading ? "Uploading…" : "Add photo"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadImages(Array.from(e.target.files || []))}
                />
              </div>
              {data.images?.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                  {data.images.map((url, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden group">
                      <img src={url} alt={`recipe-${i}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-rf-ink/80 text-rf-cream rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Ingredients</label>
                <button
                  data-testid="sandbox-add-ingredient"
                  type="button"
                  onClick={() => {
                    setData((d) => ({ ...d, ingredients: [...d.ingredients, { name: "", quantity: 0, unit: "", notes: "" }] }));
                    setIngIds((prev) => [...prev, newRid("ing")]);
                  }}
                  className="text-rf-orange text-xs font-bold inline-flex items-center gap-1"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
              <p className="text-xs text-rf-ink-soft mb-2">Drag the <span className="inline-block align-middle">⠿</span> handle to reorder.</p>
              <div className="space-y-2">
                <SortableList
                  items={data.ingredients}
                  getId={(_, i) => ingIds[i] ?? `ing-fallback-${i}`}
                  onReorder={(newIngs) => {
                    const oldIngs = data.ingredients;
                    setData((d) => ({ ...d, ingredients: newIngs }));
                    setIngIds((prev) => {
                      const map = new Map(oldIngs.map((it, i) => [it, prev[i]]));
                      return newIngs.map((it) => map.get(it));
                    });
                  }}
                  testidPrefix="sandbox-ingredient-row"
                  renderItem={(ing, i) => (
                    /* Mobile: 2-line layout so the Unit input isn't clipped
                       to 2 characters. Desktop (≥sm) keeps single-row 6/2/2/2. */
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <input value={ing.name} onChange={(e) => updateIngredient(i, "name", e.target.value)} placeholder="Ingredient" className="input-rf col-span-12 sm:col-span-6" />
                      <input type="number" value={ing.quantity} onChange={(e) => updateIngredient(i, "quantity", +e.target.value)} placeholder="Qty" className="input-rf col-span-4 sm:col-span-2" />
                      <input value={ing.unit} onChange={(e) => updateIngredient(i, "unit", e.target.value)} placeholder="Unit" className="input-rf col-span-6 sm:col-span-2" />
                      <button
                        type="button"
                        onClick={() => {
                          const arr = data.ingredients.filter((_, idx) => idx !== i);
                          if (arr.length) {
                            setData((d) => ({ ...d, ingredients: arr }));
                            setIngIds((prev) => prev.filter((_, idx) => idx !== i));
                          } else {
                            // Never leave the list empty — reset to a single blank row.
                            setData((d) => ({ ...d, ingredients: [{ name: "", quantity: 0, unit: "", notes: "" }] }));
                            setIngIds([newRid("ing")]);
                          }
                        }}
                        aria-label="Remove ingredient"
                        className="col-span-2 min-h-[44px] rounded-lg text-rf-ink-soft hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  )}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Instructions</label>
                <button
                  data-testid="sandbox-add-step"
                  type="button"
                  onClick={() => {
                    setData((d) => ({ ...d, instructions: [...d.instructions, ""] }));
                    setStepIds((prev) => [...prev, newRid("step")]);
                  }}
                  className="text-rf-orange text-xs font-bold inline-flex items-center gap-1"
                >
                  <Plus size={14} /> Add step
                </button>
              </div>
              <p className="text-xs text-rf-ink-soft mb-2">Drag the <span className="inline-block align-middle">⠿</span> handle to reorder. Step numbers renumber automatically.</p>
              <div className="space-y-2">
                <SortableList
                  items={data.instructions}
                  getId={(_, i) => stepIds[i] ?? `step-fallback-${i}`}
                  onReorder={(newSteps) => {
                    const oldSteps = data.instructions;
                    setData((d) => ({ ...d, instructions: newSteps }));
                    setStepIds((prev) => {
                      // Strings can duplicate, so match first-unused position.
                      const used = new Array(prev.length).fill(false);
                      return newSteps.map((s) => {
                        for (let k = 0; k < oldSteps.length; k++) {
                          if (!used[k] && oldSteps[k] === s) { used[k] = true; return prev[k]; }
                        }
                        return newRid("step");
                      });
                    });
                  }}
                  testidPrefix="sandbox-instruction-row"
                  renderItem={(step, i) => (
                    <div className="flex gap-2 items-start">
                      <div className="w-9 h-9 rounded-full bg-rf-amber text-rf-ink flex items-center justify-center font-bold shrink-0">{i + 1}</div>
                      <AutoGrowTextarea value={step} onChange={(e) => updateStep(i, e.target.value)} className="input-rf flex-1 resize-none" placeholder="Describe the step…" />
                      <button
                        type="button"
                        onClick={() => {
                          const arr = data.instructions.filter((_, idx) => idx !== i);
                          if (arr.length) {
                            setData((d) => ({ ...d, instructions: arr }));
                            setStepIds((prev) => prev.filter((_, idx) => idx !== i));
                          } else {
                            setData((d) => ({ ...d, instructions: [""] }));
                            setStepIds([newRid("step")]);
                          }
                        }}
                        className="text-rf-ink-soft hover:text-red-600 p-2"
                      ><Trash size={18} /></button>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="card-recipe bg-rf-cream-alt">
              <label className="flex items-start gap-3 cursor-pointer mb-3">
                <input data-testid="sandbox-public" type="checkbox" checked={data.is_public} onChange={(e) => setData({ ...data, is_public: e.target.checked })} className="mt-1 w-5 h-5 accent-rf-orange" />
                <div>
                  <p className="font-display font-bold text-rf-ink">List in public directory</p>
                  <p className="text-sm text-rf-ink-soft">Anyone can find and view this recipe.</p>
                </div>
              </label>
              {data.is_public && (
                <label className="flex items-start gap-3 cursor-pointer pl-8">
                  <input data-testid="sandbox-copyright" type="checkbox" checked={data.copyright_certified} onChange={(e) => setData({ ...data, copyright_certified: e.target.checked })} className="mt-1 w-5 h-5 accent-rf-orange" />
                  <p className="text-sm text-rf-ink-soft">I certify I own the rights to distribute this recipe text publicly.</p>
                </label>
              )}
              <label className="flex items-start gap-3 cursor-pointer mt-3">
                <input data-testid="sandbox-secret" type="checkbox" checked={data.is_secret} onChange={(e) => setData({ ...data, is_secret: e.target.checked })} className="mt-1 w-5 h-5 accent-rf-orange" />
                <div>
                  <p className="font-display font-bold text-rf-ink">Mark as Secret Recipe</p>
                  <p className="text-sm text-rf-ink-soft">Hidden from &ldquo;Selective (Minus Secrets)&rdquo; shares.</p>
                </div>
              </label>
              {/* Opt in to AI nutritional details. Off by default — we only
                  spend LLM credits + display a macro card when the cook
                  explicitly wants it. */}
              <label className="flex items-start gap-3 cursor-pointer mt-3">
                <input
                  data-testid="sandbox-show-nutrition"
                  type="checkbox"
                  checked={data.show_nutrition === true}
                  onChange={(e) => setData({ ...data, show_nutrition: e.target.checked })}
                  className="mt-1 w-5 h-5 accent-rf-orange"
                />
                <div>
                  <p className="font-display font-bold text-rf-ink">Show nutritional details</p>
                  <p className="text-sm text-rf-ink-soft">Tick to add an AI-drafted per-serving macro card (kcal / protein / carbs / fat …) at the end of the recipe. Clearly marked as an estimate — not medical advice.</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={() => nav("/dashboard")} className="btn-outline-ink">Cancel</button>
              <button data-testid="sandbox-save" disabled={saving || !book} onClick={save} className="btn-amber">
                {saving ? "Saving…" : <><FloppyDisk size={18} /> Save recipe</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PaywallModal
        open={paywall.open}
        onClose={() => setPaywall({ open: false, message: "" })}
        message={paywall.message}
        upgradesEnabled={flags.subscriptions_enabled}
        recipeCount={user?.recipe_count}
        freeLimit={limits?.free_recipe_limit}
      />
    </div>
  );
}
